import { useEffect, useState } from "react";
import { toast } from "sonner";
import api, { apiError } from "@/lib/api";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Bot, Sparkles, RefreshCw, Wand2, Info, RotateCcw } from "lucide-react";

// ─────────────────────────────────────────────────────────────────────────────
// Sector → recommended title buckets (matched on lowercased sector name)
// First matching preset wins. Order = priority.
// ─────────────────────────────────────────────────────────────────────────────
const SECTOR_BUCKET_PRESETS = [
  {
    label: "Industrie · Transport · Énergie",
    buckets: ["maintenance_ops", "procurement"],
    keywords: [
      "transport", "ferrov", "rail", "train", "metro", "bus", "automobile", "auto",
      "aviat", "aéron", "aeron", "aérosp", "aerosp", "maritime", "naval", "shipping",
      "industr", "manufactur", "product", "usine", "métall", "metall", "machine",
      "construct", "bâti", "bati", "btp", "infrastruct",
      "énerg", "energ", "oil", "gaz", "gas", "petrol", "petr", "util", "electric",
      "mining", "minier", "nuclé", "nucle", "hydrogen", "renouvel", "renewab",
      "logist", "supply", "chain", "wareh", "entrepôt",
    ],
  },
  {
    label: "Tech · SaaS · IT",
    buckets: ["tech_it", "decision_makers"],
    keywords: [
      "tech", "saas", "soft", "logici", "digit", "numér", "numer",
      "cloud", "data", "cyber", "ai", "ia", "informat", "telecom",
      "startup", "plateforme", "platform", "fintech", "edtech", "healthtech",
    ],
  },
  {
    label: "Finance · Banque · Assurance",
    buckets: ["finance", "decision_makers"],
    keywords: [
      "financ", "banc", "bank", "assur", "insur", "invest", "asset",
      "wealth", "capital", "audit", "consult", "compta",
    ],
  },
  {
    label: "Retail · Luxe · Distribution",
    buckets: ["visual_branding_retail", "procurement"],
    keywords: [
      "retail", "distrib", "luxe", "luxury", "mode", "fashion", "cosmét", "cosmet",
      "beauté", "beaut", "parfum", "horlog", "joaill", "jewel", "marque",
      "e-commerce", "ecomm", "magasin", "boutiq", "store",
      "fmcg", "consum", "agroalim", "agro", "food", "bever", "boisson",
    ],
  },
];

function suggestBucketsForSector(sectorName) {
  if (!sectorName) return null;
  const s = sectorName.toLowerCase();
  for (const preset of SECTOR_BUCKET_PRESETS) {
    if (preset.keywords.some((kw) => s.includes(kw))) return preset;
  }
  return null;
}

const COUNTRY_OPTIONS = [
  { code: "AE", label: "🇦🇪 UAE" },
  { code: "SA", label: "🇸🇦 KSA" },
  { code: "EG", label: "🇪🇬 Égypte" },
  { code: "TR", label: "🇹🇷 Türkiye" },
  { code: "FR", label: "🇫🇷 France" },
  { code: "DE", label: "🇩🇪 Allemagne" },
  { code: "IT", label: "🇮🇹 Italie" },
  { code: "ES", label: "🇪🇸 Espagne" },
  { code: "GB", label: "🇬🇧 UK" },
  { code: "US", label: "🇺🇸 USA" },
];

const SIZE_OPTIONS = ["MICRO", "SMALL", "MEDIUM", "LARGE", "ENTERPRISE"];

export default function MissionLauncherDialog({
  open,
  onOpenChange,
  defaultPrincipalId,
  defaultCountries,
  // Pre-fill block (when invoked from a Company detail page)
  defaultTargetName,
  defaultTargetWebsite,
  defaultTargetCountryId,
  defaultTargetSectorId,
  defaultTargetSize,
  defaultAltOrgNames,        // array of strings
  sourceCompanyId,           // for optional sync-back of alt names
  sourceCompanyName,         // for the "Pré-rempli depuis X" badge
  onLaunched,
}) {
  const [principals, setPrincipals] = useState([]);
  const [countries, setCountries] = useState([]);
  const [sectors, setSectors] = useState([]);
  const [buckets, setBuckets] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [suggestingAlts, setSuggestingAlts] = useState(false);

  // Compute pristine defaults so we can reset back to them on demand.
  const buildPristine = () => ({
    principal_id: defaultPrincipalId || "",
    target_name: defaultTargetName || "",
    target_website: defaultTargetWebsite || "",
    target_country_id: defaultTargetCountryId || "",
    target_sector_id: defaultTargetSectorId || "",
    target_size: defaultTargetSize || "MEDIUM",
    alt_org_names_raw: Array.isArray(defaultAltOrgNames)
      ? defaultAltOrgNames.join(", ")
      : "",
    contact_countries:
      defaultCountries && defaultCountries.length > 0
        ? defaultCountries
        : ["AE", "SA"],
    title_buckets: ["decision_makers"],
    max_contacts: 50,
    enable_neverbounce: true,
    enable_tavily_backfill: true,
    enable_email_drafts: true,
  });

  const [form, setForm] = useState(buildPristine);
  const [syncBackAltNames, setSyncBackAltNames] = useState(false);

  useEffect(() => {
    if (!open) return;
    (async () => {
      try {
        const [pRes, cRes, sRes, bRes] = await Promise.all([
          api.get("/companies", { params: { role: "PRINCIPAL", limit: 100 } }),
          api.get("/countries"),
          api.get("/sectors"),
          api.get("/missions/title-buckets"),
        ]);
        setPrincipals(pRes.data?.items || pRes.data || []);
        setCountries(cRes.data || []);
        setSectors(sRes.data || []);
        setBuckets((bRes.data?.buckets) || []);
      } catch (e) {
        toast.error(apiError(e));
      }
    })();
  }, [open]);

  // Re-prime the form whenever the dialog opens (so each launch starts from
  // the latest passed-in defaults — useful when caller invokes the dialog
  // repeatedly from different company cards).
  useEffect(() => {
    if (open) {
      setForm(buildPristine());
      setSyncBackAltNames(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    open,
    defaultPrincipalId,
    defaultTargetName,
    defaultTargetWebsite,
    defaultTargetCountryId,
    defaultTargetSectorId,
    defaultTargetSize,
    // Stringify alt names array to avoid re-running for identical content
    JSON.stringify(defaultAltOrgNames || []),
    JSON.stringify(defaultCountries || []),
  ]);

  const toggleArray = (key, value) => {
    setForm((f) => {
      const arr = new Set(f[key]);
      arr.has(value) ? arr.delete(value) : arr.add(value);
      return { ...f, [key]: Array.from(arr) };
    });
  };

  const submit = async () => {
    if (!form.principal_id || !form.target_name.trim()) {
      toast.error("Mandant et nom de la société requis");
      return;
    }
    if (form.contact_countries.length === 0) {
      toast.error("Sélectionnez au moins un pays");
      return;
    }
    if (form.title_buckets.length === 0) {
      toast.error("Sélectionnez au moins une catégorie de titres");
      return;
    }
    setSubmitting(true);
    try {
      const altNamesArr = form.alt_org_names_raw
        ? form.alt_org_names_raw.split(",").map((s) => s.trim()).filter(Boolean)
        : [];
      const payload = {
        ...form,
        alt_org_names: altNamesArr.length > 0 ? altNamesArr : null,
        target_country_id: form.target_country_id || null,
        target_sector_id: form.target_sector_id || null,
        target_website: form.target_website || null,
      };
      delete payload.alt_org_names_raw;
      const { data } = await api.post("/missions/launch", payload);
      toast.success(`🤖 Mission lancée — ${form.target_name}`);
      // Optional: sync the (possibly edited) alt names back to the source company
      if (sourceCompanyId && syncBackAltNames) {
        try {
          await api.put(`/companies/${sourceCompanyId}`, {
            alt_org_names: altNamesArr,
          });
          toast.success("Fiche société mise à jour");
        } catch (err) {
          toast.error(`Mission lancée — mais sync de la fiche a échoué : ${apiError(err)}`);
        }
      }
      onOpenChange(false);
      if (onLaunched) onLaunched(data);
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="mission-launcher">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Bot className="h-5 w-5 text-emerald-600" />
            Lancer une Mission Auto
          </DialogTitle>
          <DialogDescription>
            L'agent IA va orchestrer le flow complet : création société → analyse DNA → recherche
            contacts multi-pays → vérification emails → top prospects → drafts d'emails. ~5 min.
          </DialogDescription>
        </DialogHeader>

        {sourceCompanyName && (
          <div
            className="flex items-center gap-2 rounded-lg border border-sky-200 bg-sky-50/70 dark:bg-sky-950/30 dark:border-sky-900 px-3 py-2"
            data-testid="mission-prefill-banner"
          >
            <Info className="h-3.5 w-3.5 text-sky-600 shrink-0" />
            <span className="text-[12px] text-sky-900 dark:text-sky-200 flex-1 min-w-0 truncate">
              Pré-rempli depuis la fiche <strong>{sourceCompanyName}</strong> — tous les champs restent modifiables.
            </span>
            <Button
              type="button"
              size="sm"
              variant="ghost"
              className="h-7 text-[11px] text-sky-700 dark:text-sky-300 hover:bg-sky-100 dark:hover:bg-sky-900/50"
              onClick={() => {
                setForm(buildPristine());
                setSyncBackAltNames(false);
                toast.success("Champs réinitialisés");
              }}
              data-testid="mission-prefill-reset"
            >
              <RotateCcw className="h-3 w-3 mr-1" />
              Réinitialiser
            </Button>
          </div>
        )}

        <div className="space-y-5 py-2">
          {/* Mandant */}
          <div className="space-y-2">
            <Label>Mandant cible</Label>
            <Select
              value={form.principal_id}
              onValueChange={(v) => setForm((f) => ({ ...f, principal_id: v }))}
            >
              <SelectTrigger data-testid="mission-principal-select">
                <SelectValue placeholder="Choisir un Mandant…" />
              </SelectTrigger>
              <SelectContent>
                {principals.map((p) => (
                  <SelectItem key={p.id} value={p.id}>
                    {p.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Target company */}
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-2 col-span-2">
              <Label>Nom de la société cible *</Label>
              <Input
                value={form.target_name}
                onChange={(e) => setForm((f) => ({ ...f, target_name: e.target.value }))}
                placeholder="ex: Jameel Motors"
                data-testid="mission-target-name"
              />
            </div>
            <div className="space-y-2 col-span-2">
              <Label>Site web (optionnel)</Label>
              <Input
                value={form.target_website}
                onChange={(e) => setForm((f) => ({ ...f, target_website: e.target.value }))}
                placeholder="https://example.com"
              />
            </div>
            <div className="space-y-2">
              <Label>Pays HQ</Label>
              <Select
                value={form.target_country_id}
                onValueChange={(v) => setForm((f) => ({ ...f, target_country_id: v }))}
              >
                <SelectTrigger><SelectValue placeholder="—" /></SelectTrigger>
                <SelectContent>
                  {countries.map((c) => (
                    <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Taille estimée</Label>
              <Select
                value={form.target_size}
                onValueChange={(v) => setForm((f) => ({ ...f, target_size: v }))}
              >
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {SIZE_OPTIONS.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2 col-span-2">
              <Label>Secteur</Label>
              <Select
                value={form.target_sector_id}
                onValueChange={(v) => setForm((f) => ({ ...f, target_sector_id: v }))}
              >
                <SelectTrigger><SelectValue placeholder="—" /></SelectTrigger>
                <SelectContent>
                  {sectors.map((s) => (
                    <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2 col-span-2">
              <div className="flex items-baseline justify-between gap-2">
                <Label>Noms d'organisations alternatifs (séparés par virgule)</Label>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="h-7 text-[11px] text-emerald-700 dark:text-emerald-400 hover:bg-emerald-50 dark:hover:bg-emerald-950/30"
                  disabled={!form.target_name.trim() || suggestingAlts}
                  onClick={async () => {
                    if (!form.target_name.trim()) return;
                    setSuggestingAlts(true);
                    try {
                      const sectorName = sectors.find((s) => s.id === form.target_sector_id)?.name;
                      const countryName = countries.find((c) => c.id === form.target_country_id)?.name;
                      const { data } = await api.post("/missions/suggest-alt-names", {
                        target_name: form.target_name.trim(),
                        target_website: form.target_website || null,
                        sector_name: sectorName || null,
                        country_name: countryName || null,
                      });
                      const names = data?.alt_names || [];
                      if (names.length === 0) {
                        toast.info("Aucune suggestion trouvée — essayez d'enrichir le secteur ou le pays.");
                        return;
                      }
                      // Merge with any existing user-typed names, dedupe case-insensitive
                      const existing = (form.alt_org_names_raw || "")
                        .split(",")
                        .map((s) => s.trim())
                        .filter(Boolean);
                      const seen = new Set(existing.map((s) => s.toLowerCase()));
                      const merged = [...existing];
                      for (const n of names) {
                        if (!seen.has(n.toLowerCase())) {
                          merged.push(n);
                          seen.add(n.toLowerCase());
                        }
                      }
                      setForm((f) => ({ ...f, alt_org_names_raw: merged.join(", ") }));
                      toast.success(`✨ ${names.length} variantes ajoutées`);
                    } catch (e) {
                      toast.error(apiError(e));
                    } finally {
                      setSuggestingAlts(false);
                    }
                  }}
                  data-testid="mission-alt-names-suggest"
                >
                  {suggestingAlts ? (
                    <RefreshCw className="h-3 w-3 mr-1 animate-spin" />
                  ) : (
                    <Sparkles className="h-3 w-3 mr-1" />
                  )}
                  Suggérer
                </Button>
              </div>
              <Input
                value={form.alt_org_names_raw}
                onChange={(e) => setForm((f) => ({ ...f, alt_org_names_raw: e.target.value }))}
                placeholder="ex: Abdul Latif Jameel, BYD Türkiye"
                data-testid="mission-alt-names-input"
              />
              <p className="text-[11px] text-muted-foreground">
                Apollo cherchera aussi sous ces noms (utile pour filiales et marques).
                Cliquez <strong>Suggérer</strong> pour laisser l'analyste enrichir la liste.
              </p>
              {sourceCompanyId && (
                <label
                  className="flex items-start gap-2 cursor-pointer pt-1"
                  data-testid="mission-sync-altnames-label"
                >
                  <Checkbox
                    checked={syncBackAltNames}
                    onCheckedChange={(v) => setSyncBackAltNames(!!v)}
                    data-testid="mission-sync-altnames"
                  />
                  <span className="text-[11px] text-muted-foreground leading-snug">
                    📌 Mettre à jour aussi la fiche <strong>{sourceCompanyName}</strong> avec ces variantes
                    <span className="block text-[10px] text-muted-foreground/70">
                      Sinon, les modifications restent locales à cette mission.
                    </span>
                  </span>
                </label>
              )}
            </div>
          </div>

          {/* Countries to search */}
          <div className="space-y-2">
            <Label>Pays de recherche des contacts</Label>
            <div className="flex flex-wrap gap-2">
              {COUNTRY_OPTIONS.map((c) => {
                const on = form.contact_countries.includes(c.code);
                return (
                  <button
                    key={c.code}
                    type="button"
                    onClick={() => toggleArray("contact_countries", c.code)}
                    className={`px-3 py-1.5 rounded-full text-xs border transition ${
                      on
                        ? "bg-emerald-50 border-emerald-400 text-emerald-700 dark:bg-emerald-950/40"
                        : "bg-muted/40 border-border text-muted-foreground hover:bg-muted"
                    }`}
                    data-testid={`mission-country-${c.code}`}
                  >
                    {c.label}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Title buckets */}
          <div className="space-y-2">
            <div className="flex items-baseline justify-between gap-2">
              <Label>Catégories de profils à cibler</Label>
              {(() => {
                const sName = sectors.find((s) => s.id === form.target_sector_id)?.name;
                const preset = suggestBucketsForSector(sName);
                if (!preset) return null;
                const current = new Set(form.title_buckets);
                const already =
                  preset.buckets.every((b) => current.has(b)) &&
                  preset.buckets.length === form.title_buckets.length;
                if (already) {
                  return (
                    <span
                      className="text-[11px] text-emerald-700 dark:text-emerald-400 inline-flex items-center gap-1"
                      data-testid="mission-sector-suggestion-applied"
                    >
                      <Wand2 className="h-3 w-3" /> Recommandation appliquée
                    </span>
                  );
                }
                return null;
              })()}
            </div>

            {(() => {
              const sName = sectors.find((s) => s.id === form.target_sector_id)?.name;
              const preset = suggestBucketsForSector(sName);
              if (!preset) return null;
              const current = new Set(form.title_buckets);
              const already =
                preset.buckets.every((b) => current.has(b)) &&
                preset.buckets.length === form.title_buckets.length;
              if (already) return null;
              const labels = preset.buckets
                .map((k) => buckets.find((b) => b.key === k)?.label || k)
                .join(" + ");
              return (
                <div
                  className="flex items-center gap-3 p-3 rounded-lg border border-amber-300 bg-amber-50/60 dark:bg-amber-950/20"
                  data-testid="mission-sector-suggestion"
                >
                  <Wand2 className="h-4 w-4 text-amber-600 shrink-0" />
                  <div className="flex-1 min-w-0 text-[12px]">
                    <p className="font-medium text-amber-900 dark:text-amber-200">
                      Suggestion · secteur "{sName}"
                    </p>
                    <p className="text-amber-800/80 dark:text-amber-300/80">
                      Cibler : <strong>{labels}</strong>
                    </p>
                  </div>
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    className="border-amber-400 text-amber-800 hover:bg-amber-100 shrink-0"
                    onClick={() => setForm((f) => ({ ...f, title_buckets: preset.buckets }))}
                    data-testid="mission-sector-suggestion-apply"
                  >
                    Appliquer
                  </Button>
                </div>
              );
            })()}

            <div className="space-y-2">
              {buckets.map((b) => {
                const on = form.title_buckets.includes(b.key);
                return (
                  <div
                    key={b.key}
                    className={`flex items-start gap-3 p-3 rounded-lg border cursor-pointer transition ${
                      on ? "border-emerald-400 bg-emerald-50/50 dark:bg-emerald-950/20" : "border-border"
                    }`}
                    onClick={() => toggleArray("title_buckets", b.key)}
                  >
                    <Checkbox checked={on} data-testid={`mission-bucket-${b.key}`} />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium">{b.label}</p>
                      <p className="text-[11px] text-muted-foreground line-clamp-2">
                        {b.titles.slice(0, 5).join(" · ")} {b.titles.length > 5 ? "…" : ""}
                      </p>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Max contacts */}
          <div className="space-y-2">
            <Label>Plafond contacts à enrichir</Label>
            <Select
              value={String(form.max_contacts)}
              onValueChange={(v) => setForm((f) => ({ ...f, max_contacts: parseInt(v, 10) }))}
            >
              <SelectTrigger className="w-40"><SelectValue /></SelectTrigger>
              <SelectContent>
                {[10, 25, 50, 75, 100, 150, 200].map((n) => (
                  <SelectItem key={n} value={String(n)}>{n} contacts</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Options */}
          <div className="space-y-3 p-3 rounded-lg bg-muted/30">
            <Label className="text-sm font-semibold">Options qualité</Label>
            {[
              { k: "enable_neverbounce", label: "Validation NeverBounce (format + MX + provider)" },
              { k: "enable_tavily_backfill", label: "Backfill auto pays manquants (Tavily + Hunter fallback)" },
              { k: "enable_email_drafts", label: "Générer drafts d'emails IA personnalisés (Top 5)" },
            ].map((opt) => (
              <label key={opt.k} className="flex items-center gap-2 cursor-pointer">
                <Checkbox
                  checked={form[opt.k]}
                  onCheckedChange={(v) => setForm((f) => ({ ...f, [opt.k]: !!v }))}
                  data-testid={`mission-opt-${opt.k}`}
                />
                <span className="text-sm">{opt.label}</span>
              </label>
            ))}
          </div>

          <div className="text-[11px] text-muted-foreground flex items-center gap-2">
            <Badge variant="outline" className="text-[10px]">~5 min</Badge>
            La mission tourne en arrière-plan. Vous recevrez une notification à la fin.
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={submitting}>
            Annuler
          </Button>
          <Button onClick={submit} disabled={submitting} data-testid="mission-launch-btn">
            {submitting ? <RefreshCw className="h-4 w-4 mr-2 animate-spin" /> : <Sparkles className="h-4 w-4 mr-2" />}
            🚀 Lancer la Mission
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
